import { Button, Search, Switch } from '@ibsys/kit';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useHasPermission } from '@/entities/profile';
import { useDebouncedValue } from '@/shared/hooks';
import { useAppNavigate } from '@/shared/lib';
import { SampleCard } from './ui/SampleCard';
import { SampleList } from './ui/SampleList';
import { MasterDetailLayout } from '@/shared/ui';

export const SamplesPage: FC = () => {
  const { t } = useTranslation(['common', 'samples']);

  const { id: idOrCreating } = useParams<{ id?: string }>();
  const isCreating = idOrCreating === 'create';
  const id = idOrCreating && !isCreating ? +idOrCreating : undefined;

  const navigate = useAppNavigate();
  const canEdit = useHasPermission('ADMIN_SAMPLE');

  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleted, setShowDeleted] = useState(() => searchParams.get('deleted') === 'true');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  const handleClickAdd = () => {
    navigate('/samples/create');
  };

  const handleBack = () => {
    navigate('/samples');
  };

  const handleShowDeletedToggle = useCallback(
    (state: boolean) => {
      setSearchParams((prev) => {
        if (state) {
          prev.set('deleted', 'true');
        } else {
          prev.delete('deleted');
        }
        return prev;
      });
      setShowDeleted(state);
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(idOrCreating)}
      onBack={handleBack}
      toolbar={
        <>
          <div className="flex items-center gap-4">
            <Search
              className="flex-1"
              placeholder={t('common:search')}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {canEdit && (
              <Button onClick={handleClickAdd} className="ml-auto">
                {t('samples:addSample')}
              </Button>
            )}
          </div>

          <Switch value={showDeleted} onChange={handleShowDeletedToggle} label={t('common:showDeleted')} />
        </>
      }
      list={<SampleList showDeleted={showDeleted} searchQuery={debouncedSearchQuery} />}
      detail={<SampleCard key={id} sampleId={id} isCreating={isCreating} />}
      empty={
        <div className="flex items-center justify-center h-full text-muted-foreground">{t('samples:selectSample')}</div>
      }
    />
  );
};
